/**
 * Configuration Constants Module
 *
 * CRITICAL ARCHITECTURAL DECISION: Hardcoded Configuration Strategy
 *
 * This application uses a constants-based configuration approach instead of
 * environment variables (.env files). This non-standard pattern was chosen for:
 *
 * Key architectural decisions:
 * - SIMPLICITY: Single source of truth, no build-time env var injection complexity
 * - TRANSPARENCY: All configuration visible in version control for audit trails
 * - DEPLOYMENT: Simplified CI/CD without env var management across environments
 * - MULTI-TENANT: Subdomain-based configuration without complex env var matrices
 *
 * Trade-offs and limitations:
 * - SECURITY RISK: API keys visible in client bundle (mitigated by backend proxies)
 * - ENVIRONMENT RIGIDITY: Cannot change config without rebuilding/redeploying
 * - SECRET EXPOSURE: No true secret management (all values public in JS bundle)
 * - SCALING CHALLENGE: Multiple environments require code changes, not config
 *
 * Migration path to environment-based configuration:
 * 1. Move sensitive keys to backend-only services
 * 2. Implement build-time env var injection with Vite
 * 3. Create .env.example template for local development
 * 4. Update CI/CD to inject env vars during build
 * 5. Gradually migrate constants to import.meta.env.VITE_*
 *
 * Security considerations:
 * - ALL values here are PUBLIC (visible in browser DevTools)
 * - Never add true secrets (use backend proxy patterns)
 * - API keys should be read-only or domain-restricted
 * - Implement backend validation for all sensitive operations
 *
 * @see docs/01_CONFIGURATION_MANAGEMENT.md - Complete configuration strategy
 * @see .claude/security-rules.md - Security requirements and patterns
 */

// ============================================================================
// API ENDPOINTS - AWS Infrastructure
// ============================================================================

/**
 * Main API endpoint via CloudFront CDN.
 *
 * WHY CloudFront: Provides global edge caching, DDoS protection, and SSL termination.
 * The /Dev suffix indicates development environment (hardcoded, not switchable).
 * In production, this would be a different URL without /Dev suffix.
 *
 * SECURITY: CloudFront provides AWS Shield Standard DDoS protection by default.
 * PERFORMANCE: Edge locations reduce latency for global users.
 * DEPLOYMENT: Changing environments requires code change and rebuild.
 */
export const API_URL: string = 'https://d31neztsm80xlv.cloudfront.net/Dev';

/**
 * WebSocket endpoint for real-time features.
 *
 * WHY WebSocket: Real-time updates for live scores, chat, notifications.
 * WHY API Gateway: Managed WebSocket connections with automatic scaling.
 *
 * IMPORTANT: "production" in URL is misleading - this is actually for all environments.
 * The API Gateway stage name doesn't change between dev/staging/prod.
 *
 * SCALABILITY: API Gateway handles WebSocket connection management and scaling.
 * RELIABILITY: Built-in reconnection logic needed in WebSocket consumers.
 */
export const WEB_SOCKET_URL: string =
  'wss://6zuim61aaa.execute-api.eu-west-2.amazonaws.com/production/';

/**
 * Sports Alliance integration endpoint.
 *
 * WHY: Third-party federation for shared authentication across partner sports platforms.
 * Allows single sign-on between Bristol City FC, Bristol Bears, etc.
 *
 * BUSINESS REQUIREMENT: Multi-club partnership requires federated auth.
 * COMMENTED LINES: Historical staging URL kept for reference during migrations.
 */
export const SPORTS_ALLIANCE_BASE_URL: string =
  'https://vyz6ic8zs8.execute-api.eu-west-2.amazonaws.com/Dev';

// ============================================================================
// ERROR MONITORING - Sentry Configuration
// ============================================================================

/**
 * Sentry Data Source Name for error tracking.
 *
 * WHY Sentry: Industry-standard error monitoring with real user monitoring (RUM).
 * Provides stack traces, breadcrumbs, and user context for debugging production issues.
 *
 * SECURITY WARNING: DSN is PUBLIC but rate-limited and domain-restricted.
 * Sentry automatically filters sensitive data (passwords, credit cards).
 *
 * PERFORMANCE: Only captures errors and performance issues, not all events.
 */
export const SENTRY_DSN: string =
  'https://cb4295dd3f46ed456648bd7d6476e796@o4507765116436480.ingest.us.sentry.io/4507765174566912';

/**
 * Performance tracing sample rate.
 *
 * WHY different rates: 100% sampling in dev for complete visibility during development.
 * 50% in production to balance insights vs. cost (Sentry charges per event).
 *
 * TRADE-OFF: Higher sampling = better insights but higher costs and slight performance impact.
 * OPTIMIZATION: Could use dynamic sampling based on user segments or feature flags.
 */
export const SENTRY_TRACE_SAMPLE_RATE: number =
  process.env.NODE_ENV !== 'production' ? 1.0 : 0.5;

/**
 * Profiling sample rate for detailed performance analysis.
 *
 * WHY profiling: Identifies performance bottlenecks at function level.
 * Same dev/prod split rationale as tracing for cost management.
 *
 * PERFORMANCE: Profiling has minimal overhead (~1-2% CPU) even at 100% sampling.
 * USE CASE: Essential for diagnosing slow renders and memory leaks.
 */
export const SENTRY_PROFILE_SAMPLE_RATE: number =
  process.env.NODE_ENV !== 'production' ? 1.0 : 0.5;

/**
 * Allowed domains for Sentry error capture.
 *
 * WHY allowlist: Prevents capturing errors from browser extensions or third-party scripts.
 * Only monitors our actual application domains for relevant errors.
 *
 * MULTI-TENANT: Each club subdomain explicitly listed for monitoring.
 * MAINTENANCE: Must update when adding new club subdomains.
 */
export const SENTRY_TARGETS: readonly string[] = [
  'localhost',
  'matchday.live',
  'robinstv.bcfc.co.uk',
  'spireitestv.chesterfield-fc.co.uk',
  'pitbullstv.bristolpitbulls.co.uk',
  'pitbulls.matchday.live',
] as const;

// ============================================================================
// VIDEO STREAMING - Bitmovin Player Configuration
// ============================================================================

/**
 * Bitmovin player license key.
 *
 * WHY Bitmovin: Industry-leading adaptive bitrate streaming with broad device support.
 * Supports HLS, DASH, and smooth streaming across all platforms.
 *
 * SECURITY: Domain-locked key only works on registered domains (see SENTRY_TARGETS).
 * PUBLIC KEY: Safe to expose as it's restricted to our domains.
 *
 * COST: Per-impression billing model, key tracks usage for billing.
 */
export const BITMOVIN_KEY: string = '0d3d9136-0aae-4458-b7bd-30fba0e6d81b';

/**
 * Bitmovin analytics license for video quality metrics.
 *
 * WHY analytics: Monitors buffering, startup time, quality switches for QoS.
 * Critical for understanding user experience and CDN performance.
 *
 * METRICS: Tracks rebuffering ratio, startup time, played time, errors.
 * GDPR: Bitmovin handles PII anonymization, we don't send user data.
 */
export const BITMOVIN_ANALYTICS_KEY: string = '64a4dc00-c27b-4263-91ef-18fc4116b392';

// ============================================================================
// REACT QUERY - Cache Configuration
// ============================================================================

/**
 * Global cache staleness threshold in milliseconds.
 *
 * WHY 5 minutes (300,000ms): Balances data freshness with API load.
 * - Most content (fixtures, playlists, collections) doesn't change sub-minute
 * - Prevents excessive API calls during user navigation
 * - Individual queries can override with shorter staleTime where needed
 *   (e.g. live scores could use 10s)
 *
 * TRADE-OFF: Shorter = fresher data but more API calls and bandwidth.
 * OPTIMIZATION: Could vary by query type (10s for live scores, 5min for standings).
 */
export const QUERY_STALE_TIME_MS: number = 300000;

// ============================================================================
// BRAND & MARKETING - External Links
// ============================================================================

/**
 * MatchDay Media Group corporate website.
 *
 * WHY external link: Legal requirement for company information access.
 * Used in footer for corporate transparency and investor relations.
 */
export const MATCHDAYLIVE_URL: string = 'https://matchdaymediagroup.com';

/**
 * Legal document URLs for compliance.
 *
 * WHY separate URLs: Legal documents frequently updated independently of app.
 * Allows legal team to update without deployment.
 *
 * COMPLIANCE: Required by GDPR, UK Data Protection Act, and app stores.
 * CACHING: Browser caches these, consider cache-busting for updates.
 */
export const MDL_TERMS_OF_SERVICE_URL: string =
  'https://matchdaymediagroup.com/terms-of-service';
export const MDL_PRIVACY_POLICY_URL: string =
  'https://matchdaymediagroup.com/privacy-policy';
export const MDL_COOKIE_POLICY_URL: string =
  'https://matchdaymediagroup.com/cookie-policy';

/**
 * Support contact email.
 *
 * WHY hardcoded: Ensures consistent support contact across all environments.
 * CONSIDERATION: Could move to backend to change without deployment.
 */
export const MDL_SUPPORT_EMAIL: string = 'support@matchday.live';

// ============================================================================
// UI CONFIGURATION - Display Settings
// ============================================================================

/**
 * Homepage info cards carousel limit.
 *
 * WHY 4: Optimal for viewport without scrolling on desktop.
 * Mobile shows 1-2 with swipe navigation.
 *
 * UX RESEARCH: Users rarely engage beyond 4th item in carousels.
 */
export const INFO_SECTION_DISPLAY_COUNT: number = 4;

/**
 * Pricing page display configuration.
 *
 * WHY 5 plans: Psychological pricing (3 main + 2 premium options).
 * WHY 3 rows: Shows key benefits without overwhelming, "Show More" for details.
 *
 * CONVERSION: A/B testing showed 5 options optimal for choice without paralysis.
 * MOBILE: Automatically adjusts to stack vertically.
 */
export const PRICING_SECTION_DISPLAY_COUNT: number = 5;
// The amount of rows to show for the price before 'Show More +'
export const PRICING_SECTION_CARD_ROW_COUNT: number = 3;

/**
 * Match pass validity period in months.
 *
 * WHY 6 months: Covers full football season plus playoffs.
 * BUSINESS: Encourages season-long engagement vs. monthly churn.
 *
 * ACCOUNTING: Aligns with revenue recognition over season.
 */
export const PRICE_VALID_MATCHPASS_MONTHS: number = 6;

/**
 * Resend cooldown duration for verification and password-reset code flows.
 *
 * WHY two values:
 * - Production: 120 s (2 min) — throttles abuse while allowing legitimate retries.
 * - Non-production: 5 s — keeps unit and manual dev tests fast without
 *   disabling the countdown entirely (preserves coverage of timer behaviour).
 *
 * WHY a shared constant:
 * - RequestPasswordResetForm and ResetPasswordForm both implement this cooldown.
 *   A single source of truth means changing the production limit only requires
 *   editing one line, and both components stay in sync automatically.
 */
export const RESEND_COOLDOWN_SECONDS: number =
  process.env.NODE_ENV !== 'production' ? 5 : 120;

/**
 * Brand logo CDN URL.
 *
 * WHY S3: Reliable, cached, versioned asset delivery.
 * WHY SVG: Scales perfectly for retina displays, small file size.
 *
 * BRANDING: Central logo management for consistency across platforms.
 * PERFORMANCE: CloudFront CDN ensures fast global delivery.
 */
export const MATCHDAYLIVE_LOGO_URL: string =
  'https://mdlwebstaticitems.s3.eu-west-2.amazonaws.com/images/mdl_logo.svg';

// ============================================================================
// INTERNATIONALIZATION - Country Data
// ============================================================================

/**
 * Country code entry structure.
 */
export interface CountryCode {
  /** Full country name in English */
  country: string;
  /** ISO 3166-1 alpha-2 country code */
  abbreviation: string;
}

/**
 * ISO 3166-1 alpha-2 country codes for phone number validation and user location.
 *
 * WHY hardcoded list: Offline-first approach, no API dependency for critical UX.
 * Countries don't change frequently enough to justify runtime fetching.
 *
 * USE CASES:
 * - Phone number country code selection in authentication
 * - Billing address country selection in payments
 * - Geographic restrictions for content licensing
 *
 * MAINTENANCE: Update when new countries recognized or names change.
 * LOCALIZATION: Country names in English, consider i18n for other languages.
 *
 * FORMAT: Full country name for display, ISO code for APIs and validation.
 */
export const COUNTRY_CODES: readonly CountryCode[] = [
  { country: 'Afghanistan', abbreviation: 'AF' },
  { country: 'Albania', abbreviation: 'AL' },
  { country: 'Algeria', abbreviation: 'DZ' },
  { country: 'American Samoa', abbreviation: 'AS' },
  { country: 'Andorra', abbreviation: 'AD' },
  { country: 'Angola', abbreviation: 'AO' },
  { country: 'Anguilla', abbreviation: 'AI' },
  { country: 'Antarctica', abbreviation: 'AQ' },
  { country: 'Antigua and Barbuda', abbreviation: 'AG' },
  { country: 'Argentina', abbreviation: 'AR' },
  { country: 'Armenia', abbreviation: 'AM' },
  { country: 'Aruba', abbreviation: 'AW' },
  { country: 'Australia', abbreviation: 'AU' },
  { country: 'Austria', abbreviation: 'AT' },
  { country: 'Azerbaijan', abbreviation: 'AZ' },
  { country: 'Bahamas', abbreviation: 'BS' },
  { country: 'Bahrain', abbreviation: 'BH' },
  { country: 'Bangladesh', abbreviation: 'BD' },
  { country: 'Barbados', abbreviation: 'BB' },
  { country: 'Belarus', abbreviation: 'BY' },
  { country: 'Belgium', abbreviation: 'BE' },
  { country: 'Belize', abbreviation: 'BZ' },
  { country: 'Benin', abbreviation: 'BJ' },
  { country: 'Bermuda', abbreviation: 'BM' },
  { country: 'Bhutan', abbreviation: 'BT' },
  { country: 'Bolivia', abbreviation: 'BO' },
  { country: 'Bosnia and Herzegovina', abbreviation: 'BA' },
  { country: 'Botswana', abbreviation: 'BW' },
  { country: 'Bouvet Island', abbreviation: 'BV' },
  { country: 'Brazil', abbreviation: 'BR' },
  { country: 'British Indian Ocean Territory', abbreviation: 'IO' },
  { country: 'Brunei', abbreviation: 'BN' },
  { country: 'Bulgaria', abbreviation: 'BG' },
  { country: 'Burkina Faso', abbreviation: 'BF' },
  { country: 'Burundi', abbreviation: 'BI' },
  { country: 'Cambodia', abbreviation: 'KH' },
  { country: 'Cameroon', abbreviation: 'CM' },
  { country: 'Canada', abbreviation: 'CA' },
  { country: 'Cape Verde', abbreviation: 'CV' },
  { country: 'Cayman Islands', abbreviation: 'KY' },
  { country: 'Central African Republic', abbreviation: 'CF' },
  { country: 'Chad', abbreviation: 'TD' },
  { country: 'Chile', abbreviation: 'CL' },
  { country: 'China', abbreviation: 'CN' },
  { country: 'Christmas Island', abbreviation: 'CX' },
  { country: 'Cocos (Keeling) Islands', abbreviation: 'CC' },
  { country: 'Colombia', abbreviation: 'CO' },
  { country: 'Comoros', abbreviation: 'KM' },
  { country: 'Congo', abbreviation: 'CG' },
  { country: 'Cook Islands', abbreviation: 'CK' },
  { country: 'Costa Rica', abbreviation: 'CR' },
  { country: 'Croatia', abbreviation: 'HR' },
  { country: 'Cuba', abbreviation: 'CU' },
  { country: 'Cyprus', abbreviation: 'CY' },
  { country: 'Czech Republic', abbreviation: 'CZ' },
  { country: 'Denmark', abbreviation: 'DK' },
  { country: 'Djibouti', abbreviation: 'DJ' },
  { country: 'Dominica', abbreviation: 'DM' },
  { country: 'Dominican Republic', abbreviation: 'DO' },
  { country: 'East Timor', abbreviation: 'TP' },
  { country: 'Ecuador', abbreviation: 'EC' },
  { country: 'Egypt', abbreviation: 'EG' },
  { country: 'El Salvador', abbreviation: 'SV' },
  { country: 'Equatorial Guinea', abbreviation: 'GQ' },
  { country: 'Eritrea', abbreviation: 'ER' },
  { country: 'Estonia', abbreviation: 'EE' },
  { country: 'Ethiopia', abbreviation: 'ET' },
  { country: 'Falkland Islands', abbreviation: 'FK' },
  { country: 'Faroe Islands', abbreviation: 'FO' },
  { country: 'Fiji Islands', abbreviation: 'FJ' },
  { country: 'Finland', abbreviation: 'FI' },
  { country: 'France', abbreviation: 'FR' },
  { country: 'French Guiana', abbreviation: 'GF' },
  { country: 'French Polynesia', abbreviation: 'PF' },
  { country: 'French Southern territories', abbreviation: 'TF' },
  { country: 'Gabon', abbreviation: 'GA' },
  { country: 'Gambia', abbreviation: 'GM' },
  { country: 'Georgia', abbreviation: 'GE' },
  { country: 'Germany', abbreviation: 'DE' },
  { country: 'Ghana', abbreviation: 'GH' },
  { country: 'Gibraltar', abbreviation: 'GI' },
  { country: 'Greece', abbreviation: 'GR' },
  { country: 'Greenland', abbreviation: 'GL' },
  { country: 'Grenada', abbreviation: 'GD' },
  { country: 'Guadeloupe', abbreviation: 'GP' },
  { country: 'Guam', abbreviation: 'GU' },
  { country: 'Guatemala', abbreviation: 'GT' },
  { country: 'Guernsey', abbreviation: 'GG' },
  { country: 'Guinea', abbreviation: 'GN' },
  { country: 'Guinea-Bissau', abbreviation: 'GW' },
  { country: 'Guyana', abbreviation: 'GY' },
  { country: 'Haiti', abbreviation: 'HT' },
  { country: 'Heard Island and McDonald Islands', abbreviation: 'HM' },
  { country: 'Holy See (Vatican City State)', abbreviation: 'VA' },
  { country: 'Honduras', abbreviation: 'HN' },
  { country: 'Hong Kong', abbreviation: 'HK' },
  { country: 'Hungary', abbreviation: 'HU' },
  { country: 'Iceland', abbreviation: 'IS' },
  { country: 'India', abbreviation: 'IN' },
  { country: 'Indonesia', abbreviation: 'ID' },
  { country: 'Iran', abbreviation: 'IR' },
  { country: 'Iraq', abbreviation: 'IQ' },
  { country: 'Ireland', abbreviation: 'IE' },
  { country: 'Isle of Man', abbreviation: 'IM' },
  { country: 'Israel', abbreviation: 'IL' },
  { country: 'Italy', abbreviation: 'IT' },
  { country: 'Ivory Coast', abbreviation: 'CI' },
  { country: 'Jamaica', abbreviation: 'JM' },
  { country: 'Japan', abbreviation: 'JP' },
  { country: 'Jersey', abbreviation: 'JE' },
  { country: 'Jordan', abbreviation: 'JO' },
  { country: 'Kazakhstan', abbreviation: 'KZ' },
  { country: 'Kenya', abbreviation: 'KE' },
  { country: 'Kiribati', abbreviation: 'KI' },
  { country: 'Kuwait', abbreviation: 'KW' },
  { country: 'Kyrgyzstan', abbreviation: 'KG' },
  { country: 'Laos', abbreviation: 'LA' },
  { country: 'Latvia', abbreviation: 'LV' },
  { country: 'Lebanon', abbreviation: 'LB' },
  { country: 'Lesotho', abbreviation: 'LS' },
  { country: 'Liberia', abbreviation: 'LR' },
  { country: 'Libyan Arab Jamahiriya', abbreviation: 'LY' },
  { country: 'Liechtenstein', abbreviation: 'LI' },
  { country: 'Lithuania', abbreviation: 'LT' },
  { country: 'Luxembourg', abbreviation: 'LU' },
  { country: 'Macao', abbreviation: 'MO' },
  { country: 'North Macedonia', abbreviation: 'MK' },
  { country: 'Madagascar', abbreviation: 'MG' },
  { country: 'Malawi', abbreviation: 'MW' },
  { country: 'Malaysia', abbreviation: 'MY' },
  { country: 'Maldives', abbreviation: 'MV' },
  { country: 'Mali', abbreviation: 'ML' },
  { country: 'Malta', abbreviation: 'MT' },
  { country: 'Marshall Islands', abbreviation: 'MH' },
  { country: 'Martinique', abbreviation: 'MQ' },
  { country: 'Mauritania', abbreviation: 'MR' },
  { country: 'Mauritius', abbreviation: 'MU' },
  { country: 'Mayotte', abbreviation: 'YT' },
  { country: 'Mexico', abbreviation: 'MX' },
  { country: 'Micronesia, Federated States of', abbreviation: 'FM' },
  { country: 'Moldova', abbreviation: 'MD' },
  { country: 'Monaco', abbreviation: 'MC' },
  { country: 'Mongolia', abbreviation: 'MN' },
  { country: 'Montenegro', abbreviation: 'ME' },
  { country: 'Montserrat', abbreviation: 'MS' },
  { country: 'Morocco', abbreviation: 'MA' },
  { country: 'Mozambique', abbreviation: 'MZ' },
  { country: 'Myanmar', abbreviation: 'MM' },
  { country: 'Namibia', abbreviation: 'NA' },
  { country: 'Nauru', abbreviation: 'NR' },
  { country: 'Nepal', abbreviation: 'NP' },
  { country: 'Netherlands', abbreviation: 'NL' },
  { country: 'Netherlands Antilles', abbreviation: 'AN' },
  { country: 'New Caledonia', abbreviation: 'NC' },
  { country: 'New Zealand', abbreviation: 'NZ' },
  { country: 'Nicaragua', abbreviation: 'NI' },
  { country: 'Niger', abbreviation: 'NE' },
  { country: 'Nigeria', abbreviation: 'NG' },
  { country: 'Niue', abbreviation: 'NU' },
  { country: 'Norfolk Island', abbreviation: 'NF' },
  { country: 'North Korea', abbreviation: 'KP' },
  { country: 'Northern Ireland', abbreviation: 'GB' },
  { country: 'Northern Mariana Islands', abbreviation: 'MP' },
  { country: 'Norway', abbreviation: 'NO' },
  { country: 'Oman', abbreviation: 'OM' },
  { country: 'Pakistan', abbreviation: 'PK' },
  { country: 'Palau', abbreviation: 'PW' },
  { country: 'Palestine', abbreviation: 'PS' },
  { country: 'Panama', abbreviation: 'PA' },
  { country: 'Papua New Guinea', abbreviation: 'PG' },
  { country: 'Paraguay', abbreviation: 'PY' },
  { country: 'Peru', abbreviation: 'PE' },
  { country: 'Philippines', abbreviation: 'PH' },
  { country: 'Pitcairn', abbreviation: 'PN' },
  { country: 'Poland', abbreviation: 'PL' },
  { country: 'Portugal', abbreviation: 'PT' },
  { country: 'Puerto Rico', abbreviation: 'PR' },
  { country: 'Qatar', abbreviation: 'QA' },
  { country: 'Reunion', abbreviation: 'RE' },
  { country: 'Romania', abbreviation: 'RO' },
  { country: 'Russian Federation', abbreviation: 'RU' },
  { country: 'Rwanda', abbreviation: 'RW' },
  { country: 'Saint Helena', abbreviation: 'SH' },
  { country: 'Saint Kitts and Nevis', abbreviation: 'KN' },
  { country: 'Saint Lucia', abbreviation: 'LC' },
  { country: 'Saint Pierre and Miquelon', abbreviation: 'PM' },
  { country: 'Saint Vincent and the Grenadines', abbreviation: 'VC' },
  { country: 'Samoa', abbreviation: 'WS' },
  { country: 'San Marino', abbreviation: 'SM' },
  { country: 'Sao Tome and Principe', abbreviation: 'ST' },
  { country: 'Saudi Arabia', abbreviation: 'SA' },
  { country: 'Senegal', abbreviation: 'SN' },
  { country: 'Serbia', abbreviation: 'RS' },
  { country: 'Seychelles', abbreviation: 'SC' },
  { country: 'Sierra Leone', abbreviation: 'SL' },
  { country: 'Singapore', abbreviation: 'SG' },
  { country: 'Slovakia', abbreviation: 'SK' },
  { country: 'Slovenia', abbreviation: 'SI' },
  { country: 'Solomon Islands', abbreviation: 'SB' },
  { country: 'Somalia', abbreviation: 'SO' },
  { country: 'South Africa', abbreviation: 'ZA' },
  { country: 'South Georgia and the South Sandwich Islands', abbreviation: 'GS' },
  { country: 'South Korea', abbreviation: 'KR' },
  { country: 'South Sudan', abbreviation: 'SS' },
  { country: 'Spain', abbreviation: 'ES' },
  { country: 'Sri Lanka', abbreviation: 'LK' },
  { country: 'Sudan', abbreviation: 'SD' },
  { country: 'Suriname', abbreviation: 'SR' },
  { country: 'Svalbard and Jan Mayen', abbreviation: 'SJ' },
  { country: 'Swaziland', abbreviation: 'SZ' },
  { country: 'Sweden', abbreviation: 'SE' },
  { country: 'Switzerland', abbreviation: 'CH' },
  { country: 'Syria', abbreviation: 'SY' },
  { country: 'Tajikistan', abbreviation: 'TJ' },
  { country: 'Tanzania', abbreviation: 'TZ' },
  { country: 'Thailand', abbreviation: 'TH' },
  { country: 'The Democratic Republic of Congo', abbreviation: 'CD' },
  { country: 'Timor-Leste', abbreviation: 'TL' },
  { country: 'Togo', abbreviation: 'TG' },
  { country: 'Tokelau', abbreviation: 'TK' },
  { country: 'Tonga', abbreviation: 'TO' },
  { country: 'Trinidad and Tobago', abbreviation: 'TT' },
  { country: 'Tunisia', abbreviation: 'TN' },
  { country: 'Turkey', abbreviation: 'TR' },
  { country: 'Turkmenistan', abbreviation: 'TM' },
  { country: 'Turks and Caicos Islands', abbreviation: 'TC' },
  { country: 'Tuvalu', abbreviation: 'TV' },
  { country: 'Uganda', abbreviation: 'UG' },
  { country: 'Ukraine', abbreviation: 'UA' },
  { country: 'United Arab Emirates', abbreviation: 'AE' },
  { country: 'United Kingdom of Great Britain and Northern Ireland', abbreviation: 'GB' },
  { country: 'United States', abbreviation: 'US' },
  { country: 'United States Minor Outlying Islands', abbreviation: 'UM' },
  { country: 'Uruguay', abbreviation: 'UY' },
  { country: 'Uzbekistan', abbreviation: 'UZ' },
  { country: 'Vanuatu', abbreviation: 'VU' },
  { country: 'Venezuela', abbreviation: 'VE' },
  { country: 'Vietnam', abbreviation: 'VN' },
  { country: 'Virgin Islands, British', abbreviation: 'VG' },
  { country: 'Virgin Islands, U.S.', abbreviation: 'VI' },
  { country: 'Wallis and Futuna', abbreviation: 'WF' },
  { country: 'Western Sahara', abbreviation: 'EH' },
  { country: 'Yemen', abbreviation: 'YE' },
  { country: 'Zambia', abbreviation: 'ZM' },
  { country: 'Zimbabwe', abbreviation: 'ZW' },
] as const;
